import React, { useEffect, useMemo } from "react";
import { colors2, UI_CAPTION_DF } from "oolib";

import { useGetAnnotationsLazy } from "@/utils/react-query-hooks/getData";
import { MasonryCardGridShell } from "@/components/analysisModules/AnnotationExplorer/comps/MasonryCardGridShell";
import OverlayScrollbar from "@/components/UI/OverlayScrollbar";
import { FadeGradientWhenScroll } from "@/components/dataviz/common/FadeGradientWhenScroll";

import { AnnoCard } from "../../../WidgetAnnotationExplorer/comps/AnnoCard";
import {
  WidgetEmptyState,
  WidgetErrorState,
  WidgetLoadingState,
} from "../../../../WidgetRenderer/comps/altStatePlaceholders";
import { useUpdateLoadedWidgetsTracker } from "../../../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { useReportLandingContext } from "../../../../../ReportLandingContext";

import { AnnotationPicker } from "./comps/AnnotationPicker";
import {
  ANNOTATION_TRANSLATION_CONFIG,
  buildPickedAnnotationsFilter,
} from "./pickedAnnotations";

/**
 * TempPickedAnnotations — show a handful of hand-picked annotations.
 *
 * A researcher who wants 2 specific quotes out of 70,000 searches for them in
 * the config panel (Elastic, the same fields the Annotation Explorer page
 * searches), sees the actual text, and ticks the ones to keep. The widget then
 * renders exactly those, in the order they were picked, as the same annotation
 * cards the Annotation Explorer widget uses.
 *
 * Deliberately NOT filter-driven: report-level filters are not applied here.
 * A hand-picked set is an editorial choice — if a report filter could empty it,
 * a slide built around two quotes would silently lose them. Filtering is what
 * the Annotation Explorer widget is for.
 */

export const optionDef = {
  display: "Picked Annotations",
  icon: "Quotes",
  desc: "Search and pin specific annotations",
};

export const someReqPropsUnset = (widgetConfig) => !widgetConfig?.props?.picked?.length;

export const Component = ({
  picked = [],
  id,
  widgetIdx = 0,
  columnSpan,
  cardConfigs,
  prefetchedData,
}) => {
  const { updateWidgetDataStore, reportId } = useReportLandingContext() || {};

  const pickedIds = useMemo(
    () => picked.map((p) => p?.value).filter(Boolean),
    [picked]
  );

  const {
    data: dataFromApi,
    status: statusFromApi,
    error,
  } = useGetAnnotationsLazy({
    dataSource: "annotations",
    activeFilters: pickedIds.length ? [buildPickedAnnotationsFilter(pickedIds)] : [],
    // One page holds the whole selection — this widget is for a handful.
    limit: Math.max(pickedIds.length, 1),
    translationConfig: ANNOTATION_TRANSLATION_CONFIG,
    queryId: id,
    queryOptions: { enabled: pickedIds.length > 0 && !prefetchedData },
  });

  const data = prefetchedData || dataFromApi;
  const status = prefetchedData ? "success" : statusFromApi;

  useUpdateLoadedWidgetsTracker({ status, widgetIdx });

  /**
   * Back in the picked order — a filter returns whatever order the index
   * feels like, and the point of hand-picking is that the order is yours.
   * Anything that no longer resolves is counted, not silently dropped.
   */
  const { orderedAnnotations, missingCount } = useMemo(() => {
    if (status !== "success" || !data?.pages) {
      return { orderedAnnotations: [], missingCount: 0 };
    }
    const byId = new Map(
      data.pages
        .flatMap((page) => page?.data || [])
        // LexicalTextEditor throws on an empty editor state, so an annotation
        // with no text can't be rendered — same guard as the Explorer widget.
        .filter((d) => d?.annotations?.fragment?.allText?.trim())
        .map((d) => [String(d._id), d])
    );
    const ordered = pickedIds.map((pid) => byId.get(pid)).filter(Boolean);
    return { orderedAnnotations: ordered, missingCount: pickedIds.length - ordered.length };
  }, [status, data, pickedIds]);

  // Public-share replay: capture what was rendered, in the shape this widget
  // reads back from prefetchedData above.
  useEffect(() => {
    if (prefetchedData || status !== "success" || !orderedAnnotations.length) return;
    updateWidgetDataStore &&
      updateWidgetDataStore(id, { pages: [{ data: orderedAnnotations }] });
  }, [prefetchedData, status, orderedAnnotations, id, updateWidgetDataStore]);

  if (status === "loading") return <WidgetLoadingState />;
  if (status === "error") return <WidgetErrorState error={error} />;

  if (!orderedAnnotations.length) {
    return (
      <WidgetEmptyState
        display={
          missingCount > 0
            ? "The picked annotations are no longer available"
            : "No annotations picked yet"
        }
      />
    );
  }

  const numberOfCards = columnSpan >= 9 ? 3 : columnSpan >= 6 ? 2 : 1;

  return (
    <div style={{ maxHeight: "500px", position: "relative", display: "flex", padding: "1.6rem", paddingBottom: 0 }}>
      <OverlayScrollbar style={{ flex: 1 }} overlay={false}>
        <MasonryCardGridShell
          dataFetchStatus={status}
          dataFetchError={error}
          isFetchingNextPage={false}
          data={orderedAnnotations}
          renderCard={(cols, colI, singleAnno) => (
            <AnnoCard
              key={singleAnno._id + singleAnno.annoKey}
              singleAnno={singleAnno}
              cardConfigs={cardConfigs}
              widgetName="Picked Annotations"
              reportId={reportId}
              widgetId={id}
              widgetIdx={widgetIdx}
            />
          )}
          forceCol={numberOfCards}
          style={{ gap: "0.8rem" }}
        />
        {missingCount > 0 && (
          <UI_CAPTION_DF style={{ color: colors2.grey50, padding: "0.8rem 0" }}>
            {missingCount} picked annotation{missingCount > 1 ? "s are" : " is"} no longer
            available — re-pick {missingCount > 1 ? "them" : "it"} in the config panel.
          </UI_CAPTION_DF>
        )}
        <FadeGradientWhenScroll />
      </OverlayScrollbar>
    </div>
  );
};

export const ConfigForm = AnnotationPicker;
